import { PdfDocument } from "../render/pdfDocument.ts";
import { PAGE_CLASS } from "./pdfHighlights.ts";

export interface PdfViewOptions {
  /** Nombre visible del documento. */
  title: string;
  /** Bytes del PDF. */
  data: Blob;
}

/** Páginas de más que se dibujan fuera de pantalla, para que el scroll no espere. */
const RENDER_MARGIN_PX = 800;

/** Tope de resolución. Más que esto no se nota y come memoria. */
const MAX_SCALE = 2;

/** Páginas dibujadas a la vez antes de empezar a soltar las que no se ven. */
const MAX_RENDERED_PAGES = 12;

interface PageSlot {
  el: HTMLElement;
  canvasHost: HTMLElement;
  textLayer: HTMLElement;
  /** Ancho natural de la página en puntos, para calcular la escala mostrada. */
  pageWidthPt: number;
  rendered: boolean;
  rendering: boolean;
}

/**
 * Lector de PDF: hermano de MarkdownView.
 *
 * Misma forma (constructor, `dispose`, controles en el rail) y sin marcador:
 * un PDF se lee, no se escribe.
 */
export class PdfView {
  private root: HTMLElement;
  private pagesEl: HTMLElement;
  private titleEl: HTMLElement;
  private slots: PageSlot[] = [];
  private doc: PdfDocument | null = null;
  private observer: IntersectionObserver | null = null;
  private resizeObs: ResizeObserver | null = null;
  private disposed = false;
  private dpr = Math.max(1, window.devicePixelRatio || 1);

  constructor(private host: HTMLElement, private opts: PdfViewOptions) {
    this.root = document.createElement("div");
    this.root.className = "pdf-view";

    const header = document.createElement("div");
    header.className = "md-doc-header";
    const headerInner = document.createElement("div");
    headerInner.className = "md-doc-header-inner";
    this.titleEl = document.createElement("span");
    this.titleEl.className = "md-title";
    this.titleEl.textContent = opts.title;
    headerInner.append(this.titleEl);
    header.append(headerInner);

    this.pagesEl = document.createElement("div");
    this.pagesEl.className = "pdf-pages";

    this.root.append(header, this.pagesEl);
    this.host.appendChild(this.root);
  }

  async init(): Promise<void> {
    const doc = await PdfDocument.open(this.opts.data);
    if (this.disposed) {
      doc.destroy();
      return;
    }
    this.doc = doc;

    // Se mide sólo la primera página y se usa de molde para los huecos. Pedir
    // el tamaño real de las 300 páginas antes de dibujar nada retrasaría la
    // apertura; cada página corrige su alto al dibujarse. En un PDF de tamaño
    // mixto eso se ve como un pequeño reacomodo, que es barato comparado.
    const molde = await doc.pageSize(0);

    for (let i = 0; i < doc.pageCount; i++) {
      this.slots.push(this.buildPage(i, molde.width, molde.height));
    }

    // Sólo se dibuja lo que está (casi) a la vista: un PDF largo rasterizado
    // entero funde la memoria.
    this.observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const i = Number((e.target as HTMLElement).dataset.page);
          if (e.isIntersecting) {
            void this.renderPage(i);
          } else if (this.renderedCount() > MAX_RENDERED_PAGES) {
            // Sólo se suelta bajo presión: recorrer un PDF largo de punta a
            // punta no debe dejar 300 páginas rasterizadas en memoria.
            this.releasePage(i);
          }
        }
      },
      { root: this.root, rootMargin: `${RENDER_MARGIN_PX}px 0px` },
    );
    for (const slot of this.slots) this.observer.observe(slot.el);

    // Al cambiar el ancho sólo hay que recalcular la escala de la capa de
    // texto. La imagen se estira sola (va al 100%) y los resaltados están en
    // porcentajes, así que no se redibuja nada.
    this.resizeObs = new ResizeObserver(() => {
      for (const slot of this.slots) {
        if (slot.rendered) this.applyScale(slot);
      }
    });
    this.resizeObs.observe(this.pagesEl);
  }

  private buildPage(index: number, width: number, height: number): PageSlot {
    const el = document.createElement("div");
    el.className = PAGE_CLASS;
    el.dataset.page = String(index);
    el.style.aspectRatio = `${width} / ${height}`;

    const canvasHost = document.createElement("div");
    canvasHost.className = "pdf-page-canvas";
    // La capa de texto va ARRIBA de todo: es la que recibe la selección.
    const textLayer = document.createElement("div");
    textLayer.className = "pdf-text-layer textLayer";

    el.append(canvasHost, textLayer);
    this.pagesEl.append(el);
    return {
      el,
      canvasHost,
      textLayer,
      pageWidthPt: width,
      rendered: false,
      rendering: false,
    };
  }

  private async renderPage(index: number): Promise<void> {
    const slot = this.slots[index];
    if (!slot || !this.doc || slot.rendered || slot.rendering) return;
    slot.rendering = true;
    try {
      const { width: ptWidth, height: ptHeight } = await this.doc.pageSize(index);
      if (this.disposed) return;
      slot.el.style.aspectRatio = `${ptWidth} / ${ptHeight}`;
      slot.pageWidthPt = ptWidth;

      // La imagen se dibuja al tamaño en que se ve, por la densidad de pantalla:
      // ni borrosa ni desperdiciando texels.
      const displayScale = this.displayScale(slot);
      const renderScale = Math.min(MAX_SCALE, displayScale * this.dpr);
      const { canvas } = await this.doc.renderPage(index, renderScale);
      if (this.disposed) return;
      canvas.style.width = "100%";
      canvas.style.height = "100%";
      slot.canvasHost.replaceChildren(canvas);

      await this.doc.textLayer(index, slot.textLayer);
      if (this.disposed) return;
      this.applyScale(slot);

      slot.rendered = true;
    } catch (err) {
      console.error("pdf: no se pudo dibujar la página", index, err);
    } finally {
      slot.rendering = false;
    }
  }

  private renderedCount(): number {
    let n = 0;
    for (const s of this.slots) if (s.rendered) n++;
    return n;
  }

  /**
   * Descarta lo dibujado de una página, dejando el hueco con su proporción para
   * que el scroll no salte. Se vuelve a dibujar sola si se la mira de nuevo.
   */
  private releasePage(index: number): void {
    const slot = this.slots[index];
    if (!slot || !slot.rendered) return;
    slot.canvasHost.replaceChildren();
    slot.textLayer.replaceChildren();
    slot.rendered = false;
  }

  /** Cuánto se agranda la página respecto de su tamaño natural en puntos. */
  private displayScale(slot: PageSlot): number {
    const shown = slot.el.clientWidth;
    return shown > 0 && slot.pageWidthPt > 0 ? shown / slot.pageWidthPt : 1;
  }

  /**
   * Calza la capa de texto con el tamaño en que se muestra la página.
   *
   * Como pdf.js deja las posiciones en unidades de página, cambiar de ancho es
   * sólo mover esta variable — no hay que rehacer nada.
   */
  private applyScale(slot: PageSlot): void {
    slot.textLayer.style.setProperty("--total-scale-factor", String(this.displayScale(slot)));
  }

  dispose(): void {
    this.disposed = true;
    this.observer?.disconnect();
    this.observer = null;
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.doc?.destroy();
    this.doc = null;
    this.slots = [];
    this.root.remove();
  }
}
